import { useCallback, useEffect, useState } from "react";
import { api, downloadAdminExport } from "@/lib/apiClient";
import { formatIST, formatISTDate } from "@/lib/utils";
import { AdminLayout } from "@/components/AdminLayout";
import { DoctorPhoto } from "@/components/DoctorPhoto";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Link2, Loader2, Plus, Copy, Ban, Eye, ChevronLeft, ChevronRight, Download,
} from "lucide-react";

// Named public pledge links (/pledge/<name>): create, track counts, and see
// exactly who pledged through each link.
export default function AdminLinksPage() {
  const [links, setLinks] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null); // link name whose pledges are open
  const [pledges, setPledges] = useState({ items: [], total: 0 });
  const [pledgePage, setPledgePage] = useState(1);
  const [pledgesLoading, setPledgesLoading] = useState(false);
  const limit = 25;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const urlFor = (name) => `${origin}/pledge/${name}`;

  const loadLinks = useCallback(() => {
    api.get("/admin/links").then((r) => setLinks(r.data)).catch(() => setLinks([]));
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  useEffect(() => {
    if (!viewing) return;
    setPledgesLoading(true);
    api.get(`/admin/links/${viewing}/pledges`, { params: { page: pledgePage, limit } })
      .then((r) => setPledges(r.data))
      .catch(() => {})
      .finally(() => setPledgesLoading(false));
  }, [viewing, pledgePage]);

  // Promotional link: name + brand creative shown at the bottom of the page.
  const [promoName, setPromoName] = useState("");
  const [promoLogo, setPromoLogo] = useState(null); // data URL
  const [promoCreating, setPromoCreating] = useState(false);

  const onPromoLogoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 1.4 * 1024 * 1024) { toast.error("Image too large — keep the creative under 1.4 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setPromoLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const createPromoLink = async (e) => {
    e?.preventDefault();
    const name = promoName.trim().toLowerCase();
    if (!name) { toast.error("Enter a link name."); return; }
    if (!promoLogo) { toast.error("Upload the brand creative image."); return; }
    setPromoCreating(true);
    try {
      await api.post("/admin/links/promo", { name, logo: promoLogo });
      toast.success(`Promotional link created: /pledge/${name}`);
      setPromoName("");
      setPromoLogo(null);
      loadLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create promotional link");
    } finally {
      setPromoCreating(false);
    }
  };

  const createLink = async (e) => {
    e?.preventDefault();
    const name = newName.trim().toLowerCase();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/admin/links", { name });
      toast.success(`Link created: /pledge/${name}`);
      setNewName("");
      loadLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create link");
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (name) => {
    if (!window.confirm(`Deactivate /pledge/${name}? The link will stop accepting pledges (existing pledges are kept).`)) return;
    try {
      await api.delete(`/admin/links/${name}`);
      toast.success(`Deactivated /pledge/${name}`);
      loadLinks();
    } catch {
      toast.error("Could not deactivate link");
    }
  };

  const copyUrl = (name) => {
    navigator.clipboard?.writeText(urlFor(name))
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Copy failed"));
  };

  const totalPages = Math.max(1, Math.ceil(pledges.total / limit));

  return (
    <AdminLayout>
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="links-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#0056D2]" /> Pledge Links
          </h3>
          <form onSubmit={createLink} className="flex items-center gap-2">
            <span className="text-sm text-slate-400">{origin}/pledge/</span>
            <Input
              data-testid="new-link-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. manoj"
              className="w-40"
            />
            <Button data-testid="create-link-btn" type="submit" disabled={creating || !newName.trim()} className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create link</>}
            </Button>
          </form>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Each link is a public pledge page that needs no MR login. Share it privately — links are not shown anywhere on the site.
        </p>

        {/* Promotional link: plain branded page with the creative at the bottom */}
        <form onSubmit={createPromoLink} className="mt-4 rounded-xl border border-dashed border-[#FF9933]/60 bg-orange-50/40 p-4 flex flex-wrap items-center gap-3" data-testid="promo-link-form">
          <span className="text-sm font-bold text-slate-700">Promotional link:</span>
          <span className="text-sm text-slate-400">{origin}/pledge/</span>
          <Input
            data-testid="promo-link-name"
            value={promoName}
            onChange={(e) => setPromoName(e.target.value)}
            placeholder="e.g. xstan-amh"
            className="w-40"
          />
          <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 h-9 text-sm font-medium cursor-pointer hover:bg-white">
            <input type="file" accept="image/*" className="sr-only" onChange={onPromoLogoChange} data-testid="promo-logo-input" />
            {promoLogo ? "Change brand creative" : "Upload brand creative"}
          </label>
          {promoLogo && <img src={promoLogo} alt="Creative preview" className="h-10 w-auto rounded border border-slate-200" data-testid="promo-logo-preview" />}
          <Button data-testid="create-promo-link-btn" type="submit" disabled={promoCreating} className="rounded-full bg-[#FF9933] hover:bg-orange-500 text-slate-900 font-bold">
            {promoCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create promo link</>}
          </Button>
          <p className="w-full text-xs text-slate-500">
            Opens a plain page: August Kranti banner on top, short pledge form, and only this brand's creative at the bottom. Tracked like any other link.
          </p>
        </form>

        <div className="mt-4 overflow-x-auto">
          <Table data-testid="links-table">
            <TableHeader>
              <TableRow>
                <TableHead>Link</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Pledges Received</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links === null ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></TableCell></TableRow>
              ) : links.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No links yet — create the first one above</TableCell></TableRow>
              ) : (
                links.map((l) => (
                  <TableRow key={l.name} data-testid={`link-row-${l.name}`}>
                    <TableCell className="font-medium text-slate-900">/pledge/{l.name}</TableCell>
                    <TableCell>
                      {l.promo ? (
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700">Promo</Badge>
                          {l.brand_logo && <img src={l.brand_logo} alt="creative" className="h-7 w-auto rounded border border-slate-200" />}
                        </span>
                      ) : (
                        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500">Standard</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={l.active ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-500"}>
                        {l.active ? "active" : "inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 text-xs">{formatISTDate(l.created_at)}</TableCell>
                    <TableCell className="text-right font-display font-bold text-slate-900">{l.pledge_count}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button data-testid={`copy-link-${l.name}`} onClick={() => copyUrl(l.name)} size="sm" variant="ghost" className="rounded-full h-8 text-slate-500" title="Copy URL">
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                      </Button>
                      <Button data-testid={`view-link-${l.name}`} onClick={() => { setViewing(viewing === l.name ? null : l.name); setPledgePage(1); }} size="sm" variant="ghost" className="rounded-full h-8 text-[#0056D2]" title="View pledges">
                        <Eye className="w-3.5 h-3.5 mr-1" /> {viewing === l.name ? "Hide" : "View"}
                      </Button>
                      {l.active && (
                        <Button data-testid={`deactivate-link-${l.name}`} onClick={() => deactivate(l.name)} size="sm" variant="ghost" className="rounded-full h-8 text-red-600" title="Deactivate">
                          <Ban className="w-3.5 h-3.5 mr-1" /> Deactivate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {viewing && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="link-pledges-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display font-bold text-slate-900">
              Pledges via <span className="text-[#0056D2]">/pledge/{viewing}</span> ({pledges.total})
            </h3>
            <Button
              data-testid="link-export-btn"
              onClick={() => downloadAdminExport({ source_link: viewing }).then(() => toast.success("Excel exported")).catch(() => toast.error("Export failed"))}
              variant="outline" className="rounded-full border-slate-300"
            >
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Photo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Reg No</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>City / State</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Pledged At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pledgesLoading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></TableCell></TableRow>
                ) : pledges.items.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No pledges from this link yet</TableCell></TableRow>
                ) : (
                  pledges.items.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell><DoctorPhoto pledge={p} /></TableCell>
                      <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
                      <TableCell className="text-slate-600">{p.reg_no}</TableCell>
                      <TableCell className="text-slate-600">{p.specialty}</TableCell>
                      <TableCell className="text-slate-600">{p.city}, {p.state}</TableCell>
                      <TableCell className="text-slate-600">{p.email}</TableCell>
                      <TableCell className="text-slate-600">{p.mobile}</TableCell>
                      <TableCell className="text-slate-600 text-xs">{formatIST(p.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <span>Page {pledgePage} of {totalPages}</span>
            <div className="flex gap-2">
              <Button onClick={() => setPledgePage((p) => Math.max(1, p - 1))} disabled={pledgePage <= 1} size="sm" variant="outline" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
              <Button onClick={() => setPledgePage((p) => Math.min(totalPages, p + 1))} disabled={pledgePage >= totalPages} size="sm" variant="outline" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
